import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { SessionProgressService } from "Services/axios/SessionProgress.Service";
import { handleApiError } from "Features/Common/ErrorHandler";
import {
	normalizeAnalytics,
	normalizeSessionDetail,
	normalizeSessionsList,
} from "./sessionProgressAnalyticsNormalizer";

export const fetchSessionProgressAnalyticsThunk = createAsyncThunk(
	"sessionProgressAnalytics/fetchAnalytics",
	async (params = {}, { rejectWithValue }) => {
		try {
			const response = await SessionProgressService.getAnalytics(params);
			return normalizeAnalytics(response.data);
		} catch (error) {
			handleApiError(error);
			return rejectWithValue(
				error?.response?.data?.message ||
					error?.message ||
					"Gagal memuat analytics private session progress"
			);
		}
	}
);

export const fetchSessionProgressSessionsThunk = createAsyncThunk(
	"sessionProgressAnalytics/fetchSessions",
	async (params, { rejectWithValue }) => {
		try {
			const response = await SessionProgressService.getSessions(params);
			return normalizeSessionsList(response.data);
		} catch (error) {
			handleApiError(error);
			return rejectWithValue(
				error?.response?.data?.message ||
					error?.message ||
					"Gagal memuat daftar sesi private session progress"
			);
		}
	}
);

export const fetchSessionProgressDetailThunk = createAsyncThunk(
	"sessionProgressAnalytics/fetchDetail",
	async (id, { rejectWithValue }) => {
		try {
			const response = await SessionProgressService.getSessionDetail(id);
			return normalizeSessionDetail(response.data);
		} catch (error) {
			handleApiError(error);
			return rejectWithValue(
				error?.response?.data?.message ||
					error?.message ||
					"Gagal memuat detail sesi"
			);
		}
	}
);

const sessionProgressAnalyticsSlice = createSlice({
	name: "sessionProgressAnalytics",
	initialState: {
		analytics: null,
		sessions: [],
		pagination: null,
		sessionDetail: null,
		filters: {
			page: 1,
			per_page: 15,
			start_date: null,
			end_date: null,
			preset: "all",
			year: new Date().getFullYear(),
		},
		isLoadingAnalytics: false,
		isLoadingSessions: false,
		isLoadingDetail: false,
		error: null,
		detailError: null,
	},
	reducers: {
		setSessionFilters: (state, action) => {
			state.filters = { ...state.filters, ...action.payload };
		},
		setDateFilters: (state, action) => {
			state.filters = {
				...state.filters,
				...action.payload,
				page: 1,
			};
		},
		clearSessionDetail: (state) => {
			state.sessionDetail = null;
			state.detailError = null;
			state.isLoadingDetail = false;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchSessionProgressAnalyticsThunk.pending, (state) => {
				state.isLoadingAnalytics = true;
				state.error = null;
			})
			.addCase(fetchSessionProgressAnalyticsThunk.fulfilled, (state, action) => {
				state.isLoadingAnalytics = false;
				state.analytics = action.payload;
			})
			.addCase(fetchSessionProgressAnalyticsThunk.rejected, (state, action) => {
				state.isLoadingAnalytics = false;
				state.error = action.payload;
			})
			.addCase(fetchSessionProgressSessionsThunk.pending, (state) => {
				state.isLoadingSessions = true;
				state.error = null;
			})
			.addCase(fetchSessionProgressSessionsThunk.fulfilled, (state, action) => {
				state.isLoadingSessions = false;
				state.sessions = action.payload.data;
				state.pagination = action.payload.pagination;
			})
			.addCase(fetchSessionProgressSessionsThunk.rejected, (state, action) => {
				state.isLoadingSessions = false;
				state.error = action.payload;
			})
			.addCase(fetchSessionProgressDetailThunk.pending, (state) => {
				state.isLoadingDetail = true;
				state.detailError = null;
			})
			.addCase(fetchSessionProgressDetailThunk.fulfilled, (state, action) => {
				state.isLoadingDetail = false;
				state.sessionDetail = action.payload;
			})
			.addCase(fetchSessionProgressDetailThunk.rejected, (state, action) => {
				state.isLoadingDetail = false;
				state.detailError = action.payload;
			});
	},
});

export const { setSessionFilters, setDateFilters, clearSessionDetail } =
	sessionProgressAnalyticsSlice.actions;

export default sessionProgressAnalyticsSlice.reducer;
